import { Controller } from "@hotwired/stimulus"

/**
 * Business Match Selector Controller
 *
 * Handles selection of correct business when BaseLayer returns multiple matches.
 * Mobile-first design with touch support and keyboard navigation.
 *
 * Features:
 * - Touch-friendly selection on mobile
 * - Keyboard navigation for accessibility
 * - Visual confirmation before finalizing
 * - Auto-populates form fields with selected match
 * - Responsive design (stacked mobile, grid tablet/desktop)
 */
export default class extends Controller {
  static targets = [
    "match",
    "selectedDetails",
    "confirmButton",
    "compareToggle",
    "comparisonView",
    "radioButton",
    "matchCard"
  ]

  static values = {
    selected: String,
    csrfToken: String,
    applicationId: String
  }

  static classes = [
    "selected",
    "unselected"
  ]

  connect() {
    // Set up CSRF token
    this.csrfTokenValue = document.querySelector('meta[name="csrf-token"]')?.content

    // Initialize - no selection by default
    this.selectedMatchId = null

    // Add keyboard support
    this.element.addEventListener('keydown', this.handleKeyboard.bind(this))

    // Check if there's a pre-selected match
    if (this.hasSelectedValue && this.selectedValue) {
      this.selectMatch({ target: { dataset: { matchId: this.selectedValue } } })
    }

    // Announce to screen readers
    this.announceToScreenReader(`${this.matchTargets.length} potential business matches found. Please select the correct one.`)
  }

  disconnect() {
    this.element.removeEventListener('keydown', this.handleKeyboard.bind(this))
  }

  /**
   * Handle match selection (click or tap)
   */
  selectMatch(event) {
    const matchCard = event.target.closest('[data-business-match-selector-target="matchCard"]')
    if (!matchCard) return

    const matchId = matchCard.dataset.matchId
    const matchIndex = matchCard.dataset.matchIndex

    if (!matchId) {
      console.error('No match ID found on card')
      return
    }

    // Store selected match ID
    this.selectedMatchId = matchId
    this.selectedMatchIndex = matchIndex

    // Update visual selection
    this.updateSelection(matchCard)

    // Update radio button
    this.updateRadioButtons(matchId)

    // Show selected details
    this.showSelectedDetails(matchCard)

    // Enable confirm button
    this.enableConfirmButton()

    // Update form fields with selected match data
    this.updateFormFields(matchCard)

    // Scroll to confirmation on mobile
    if (this.isMobile()) {
      this.scrollToConfirmation()
    }

    // Announce to screen readers
    const businessName = matchCard.dataset.businessName || 'Business'
    this.announceToScreenReader(`Selected ${businessName}. Review details and confirm your selection.`)
  }

  /**
   * Update visual selection state
   */
  updateSelection(selectedCard) {
    // Remove selection from all cards
    this.matchCardTargets.forEach(card => {
      card.classList.remove('ring-2', 'ring-blue-600', 'border-blue-600', 'bg-blue-50')
      card.classList.add('border-gray-200', 'bg-white')

      // Update selection icon
      const icon = card.querySelector('[data-selection-icon]')
      if (icon) {
        icon.classList.add('hidden')
      }
    })

    // Add selection to clicked card
    selectedCard.classList.remove('border-gray-200', 'bg-white')
    selectedCard.classList.add('ring-2', 'ring-blue-600', 'border-blue-600', 'bg-blue-50')

    // Show selection icon
    const icon = selectedCard.querySelector('[data-selection-icon]')
    if (icon) {
      icon.classList.remove('hidden')
    }
  }

  /**
   * Update radio button states
   */
  updateRadioButtons(matchId) {
    if (!this.hasRadioButtonTarget) return

    this.radioButtonTargets.forEach(radio => {
      radio.checked = (radio.value === matchId)
    })
  }

  /**
   * Show selected business details in confirmation area
   */
  showSelectedDetails(matchCard) {
    if (!this.hasSelectedDetailsTarget) return

    const businessName = matchCard.dataset.businessName || 'Unknown'
    const businessAddress = matchCard.dataset.businessAddress || 'Unknown'
    const matchQuality = matchCard.dataset.matchQuality || 'Unknown'
    const kybRating = matchCard.dataset.kybRating || 'N/A'

    this.selectedDetailsTarget.innerHTML = `
      <div class="rounded-lg border-2 border-blue-200 bg-blue-50 p-4 sm:p-5">
        <div class="flex items-start">
          <div class="flex-shrink-0">
            <svg class="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div class="ml-3 flex-1">
            <h3 class="text-sm font-semibold text-blue-900 sm:text-base">
              Selected Business
            </h3>
            <dl class="mt-3 space-y-2 text-xs sm:text-sm">
              <div>
                <dt class="inline font-medium text-blue-900">Name:</dt>
                <dd class="inline ml-1 text-blue-700">${this.escapeHTML(businessName)}</dd>
              </div>
              <div>
                <dt class="inline font-medium text-blue-900">Address:</dt>
                <dd class="inline ml-1 text-blue-700">${this.escapeHTML(businessAddress)}</dd>
              </div>
              <div>
                <dt class="inline font-medium text-blue-900">Match Quality:</dt>
                <dd class="inline ml-1 text-blue-700">${matchQuality}</dd>
              </div>
              <div>
                <dt class="inline font-medium text-blue-900">KYB Rating:</dt>
                <dd class="inline ml-1 text-blue-700">${kybRating}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>
    `

    this.selectedDetailsTarget.classList.remove('hidden')
  }

  /**
   * Enable confirm button
   */
  enableConfirmButton() {
    if (!this.hasConfirmButtonTarget) return

    this.confirmButtonTarget.disabled = false
    this.confirmButtonTarget.classList.remove('opacity-50', 'cursor-not-allowed')
    this.confirmButtonTarget.classList.add('hover:bg-blue-700')
  }

  /**
   * Update form fields with selected match data
   */
  updateFormFields(matchCard) {
    const businessName = matchCard.dataset.businessName
    const ein = matchCard.dataset.ein
    const address = matchCard.dataset.businessAddress
    const city = matchCard.dataset.city
    const state = matchCard.dataset.state
    const zipCode = matchCard.dataset.zipCode
    const structure = matchCard.dataset.businessStructure

    // Update form fields if they exist
    this.updateFieldValue('loan_application_business_name', businessName)
    this.updateFieldValue('loan_application_ein', ein)
    this.updateFieldValue('loan_application_business_address_line_1', address)
    this.updateFieldValue('loan_application_business_city', city)
    this.updateFieldValue('loan_application_business_state', state)
    this.updateFieldValue('loan_application_business_zip_code', zipCode)
    this.updateFieldValue('loan_application_business_structure', structure)
  }

  /**
   * Update a form field value
   */
  updateFieldValue(fieldId, value) {
    if (!value) return

    const field = document.getElementById(fieldId)
    if (field) {
      field.value = value
      field.dispatchEvent(new Event('input', { bubbles: true }))
      field.dispatchEvent(new Event('change', { bubbles: true }))

      // Add visual feedback
      field.classList.add('bg-blue-50', 'border-blue-300')
      setTimeout(() => {
        field.classList.remove('bg-blue-50', 'border-blue-300')
      }, 2000)
    }
  }

  /**
   * Confirm selection and proceed
   */
  async confirm(event) {
    event.preventDefault()

    if (!this.selectedMatchId) {
      this.showError('Please select a business match first.')
      return
    }

    const matchCard = this.matchCardTargets.find(
      card => card.dataset.matchId === this.selectedMatchId
    )

    if (!matchCard) {
      this.showError('Selected match not found.')
      return
    }

    // Show loading state
    this.showConfirmLoading()

    try {
      // Get match data
      const matchData = this.extractMatchData(matchCard)

      // Save selection to backend (if endpoint exists)
      // await this.saveMatchSelection(matchData)

      // Hide selector, show success
      this.hideSelector()
      this.showConfirmationSuccess()

      // Announce to screen readers
      this.announceToScreenReader('Business match confirmed. Form fields updated.')

    } catch (error) {
      console.error('Error confirming match:', error)
      this.showError('Failed to confirm selection. Please try again.')
      this.hideConfirmLoading()
    }
  }

  /**
   * Extract all data from match card
   */
  extractMatchData(matchCard) {
    return {
      match_id: matchCard.dataset.matchId,
      business_name: matchCard.dataset.businessName,
      ein: matchCard.dataset.ein,
      address: matchCard.dataset.businessAddress,
      city: matchCard.dataset.city,
      state: matchCard.dataset.state,
      zip_code: matchCard.dataset.zipCode,
      business_structure: matchCard.dataset.businessStructure,
      match_quality: matchCard.dataset.matchQuality,
      kyb_rating: matchCard.dataset.kybRating,
      kyb_score: matchCard.dataset.kybScore
    }
  }

  /**
   * Toggle comparison view (side-by-side on desktop, overlay on mobile)
   */
  toggleComparison(event) {
    event.preventDefault()

    if (!this.hasComparisonViewTarget) return

    const isHidden = this.comparisonViewTarget.classList.contains('hidden')

    if (isHidden) {
      this.comparisonViewTarget.classList.remove('hidden')
      if (this.hasCompareToggleTarget) {
        this.compareToggleTarget.textContent = 'Hide Comparison'
      }
      this.announceToScreenReader('Comparison view opened')
    } else {
      this.comparisonViewTarget.classList.add('hidden')
      if (this.hasCompareToggleTarget) {
        this.compareToggleTarget.textContent = 'Compare Matches'
      }
      this.announceToScreenReader('Comparison view closed')
    }
  }

  /**
   * Cancel selection and go back
   */
  cancel(event) {
    event.preventDefault()

    const confirmed = confirm('Are you sure you want to cancel? Any selection will be lost.')
    if (!confirmed) return

    this.hideSelector()
    this.announceToScreenReader('Match selection cancelled')
  }

  /**
   * Change selection (from confirmation view)
   */
  changeSelection(event) {
    event.preventDefault()

    // Clear current selection
    this.selectedMatchId = null
    this.selectedMatchIndex = null

    // Hide details
    if (this.hasSelectedDetailsTarget) {
      this.selectedDetailsTarget.classList.add('hidden')
    }

    // Disable confirm button
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.disabled = true
      this.confirmButtonTarget.classList.add('opacity-50', 'cursor-not-allowed')
      this.confirmButtonTarget.classList.remove('hover:bg-blue-700')
    }

    // Clear visual selection
    this.matchCardTargets.forEach(card => {
      card.classList.remove('ring-2', 'ring-blue-600', 'border-blue-600', 'bg-blue-50')
      card.classList.add('border-gray-200', 'bg-white')

      const icon = card.querySelector('[data-selection-icon]')
      if (icon) {
        icon.classList.add('hidden')
      }
    })

    // Scroll to top of matches
    this.element.scrollIntoView({ behavior: 'smooth', block: 'start' })

    this.announceToScreenReader('Selection cleared. Please choose a business match.')
  }

  /**
   * Keyboard navigation support
   */
  handleKeyboard(event) {
    if (!this.hasMatchCardTarget) return

    const currentIndex = this.selectedMatchIndex ? parseInt(this.selectedMatchIndex) : -1

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.selectNextMatch(currentIndex)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.selectPreviousMatch(currentIndex)
        break
      case 'Enter':
        if (this.selectedMatchId && this.hasConfirmButtonTarget && !this.confirmButtonTarget.disabled) {
          event.preventDefault()
          this.confirm(event)
        }
        break
      case 'Escape':
        event.preventDefault()
        this.cancel(event)
        break
    }
  }

  /**
   * Select next match (keyboard navigation)
   */
  selectNextMatch(currentIndex) {
    const nextIndex = currentIndex + 1
    if (nextIndex < this.matchCardTargets.length) {
      const nextCard = this.matchCardTargets[nextIndex]
      this.selectMatch({ target: nextCard })
      nextCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  /**
   * Select previous match (keyboard navigation)
   */
  selectPreviousMatch(currentIndex) {
    const prevIndex = currentIndex - 1
    if (prevIndex >= 0) {
      const prevCard = this.matchCardTargets[prevIndex]
      this.selectMatch({ target: prevCard })
      prevCard.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }

  /**
   * Show loading state on confirm button
   */
  showConfirmLoading() {
    if (!this.hasConfirmButtonTarget) return

    const originalText = this.confirmButtonTarget.textContent
    this.confirmButtonTarget.dataset.originalText = originalText
    this.confirmButtonTarget.disabled = true
    this.confirmButtonTarget.innerHTML = `
      <svg class="animate-spin -ml-1 mr-2 h-4 w-4 inline" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
      Confirming...
    `
  }

  /**
   * Hide loading state
   */
  hideConfirmLoading() {
    if (!this.hasConfirmButtonTarget) return

    const originalText = this.confirmButtonTarget.dataset.originalText || 'Confirm Selection'
    this.confirmButtonTarget.textContent = originalText
    this.confirmButtonTarget.disabled = false
  }

  /**
   * Show confirmation success message
   */
  showConfirmationSuccess() {
    // Create success message
    const successMessage = document.createElement('div')
    successMessage.className = 'rounded-lg border-2 border-green-200 bg-green-50 p-4 sm:p-6 mb-6'
    successMessage.innerHTML = `
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div class="ml-3 flex-1">
          <h3 class="text-sm font-semibold text-green-900 sm:text-base">
            Business Match Confirmed
          </h3>
          <p class="mt-2 text-xs text-green-700 sm:text-sm">
            Your business information has been updated. You can now continue with your application.
          </p>
        </div>
      </div>
    `

    // Insert before the selector
    this.element.parentElement.insertBefore(successMessage, this.element)

    // Auto-remove after 5 seconds
    setTimeout(() => {
      successMessage.remove()
    }, 5000)
  }

  /**
   * Hide the entire selector
   */
  hideSelector() {
    this.element.classList.add('hidden')
  }

  /**
   * Show error message
   */
  showError(message) {
    // Create error message element
    const errorDiv = document.createElement('div')
    errorDiv.className = 'rounded-lg border-2 border-red-200 bg-red-50 p-4 mb-4'
    errorDiv.innerHTML = `
      <div class="flex items-start">
        <div class="flex-shrink-0">
          <svg class="h-5 w-5 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-1.964-1.333-2.732 0L3.732 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        <div class="ml-3 flex-1">
          <p class="text-sm text-red-700">${this.escapeHTML(message)}</p>
        </div>
      </div>
    `

    // Insert at top of selector
    this.element.insertBefore(errorDiv, this.element.firstChild)

    // Auto-remove after 5 seconds
    setTimeout(() => {
      errorDiv.remove()
    }, 5000)

    this.announceToScreenReader(`Error: ${message}`)
  }

  /**
   * Scroll to confirmation button (mobile)
   */
  scrollToConfirmation() {
    if (!this.hasConfirmButtonTarget) return

    const offset = 80 // Mobile header offset
    const elementPosition = this.confirmButtonTarget.getBoundingClientRect().top + window.pageYOffset
    const offsetPosition = elementPosition - offset

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    })
  }

  /**
   * Detect mobile viewport
   */
  isMobile() {
    return window.innerWidth < 768 // Tailwind 'md' breakpoint
  }

  /**
   * Announce to screen readers (accessibility)
   */
  announceToScreenReader(message) {
    const announcement = document.createElement('div')
    announcement.setAttribute('role', 'status')
    announcement.setAttribute('aria-live', 'polite')
    announcement.className = 'sr-only'
    announcement.textContent = message

    document.body.appendChild(announcement)

    setTimeout(() => {
      document.body.removeChild(announcement)
    }, 1000)
  }

  /**
   * Escape HTML for safe rendering
   */
  escapeHTML(str) {
    const div = document.createElement('div')
    div.textContent = str
    return div.innerHTML
  }
}
